import React from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Stack,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
  Autocomplete,
  Fade,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

interface AgentIdentity {
  name: string;
  provider: string;
  role: string;
  description: string;
}

interface KnowledgeBase {
  docIndexes: any[];
  selectedDocIndex?: string | null;
  selectedKnowledgeSpaceIds?: string[];
}

interface ToolsConfig {
  discoveredTools: any[];
  availableKeys?: any[];
  onToolKeyChange?: (toolIndex: number, key: any | null) => void;
}

interface ExpressAgentReviewOrEditProps {
  identity: AgentIdentity;
  onIdentityChange: (field: keyof AgentIdentity, value: string) => void;
  knowledgeBase: KnowledgeBase;
  onKnowledgeBaseChange: (indexIds: string[]) => void;
  tools: ToolsConfig;
  onCancel: () => void;
  onCreate: () => void;
  isCreating: boolean;
  isEditMode?: boolean;
}

const ExpressAgentReviewOrEdit: React.FC<ExpressAgentReviewOrEditProps> = ({
  identity,
  onIdentityChange,
  knowledgeBase,
  onKnowledgeBaseChange,
  tools,
  onCancel,
  onCreate,
  isCreating,
  isEditMode = false,
}) => {
  const availableKeys = Array.isArray(tools.availableKeys)
    ? tools.availableKeys
    : [];
  const missingToolCredentials = tools.discoveredTools.filter((tool) => {
    return (
      Array.isArray(tool?.keys_schema) &&
      tool.keys_schema.length > 0 &&
      !tool.selectedKey
    );
  });
  const hasMissingToolCredentials = missingToolCredentials.length > 0;

  return (
    <Fade in={true}>
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="flex-start"
        >
          {/* Identity & Role Section */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 3,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}
                >
                  Identity & Role
                </Typography>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Agent Name"
                    variant="filled"
                    value={identity.name}
                    onChange={(e) => onIdentityChange("name", e.target.value)}
                  />
                  <Autocomplete
                    freeSolo
                    forcePopupIcon
                    options={["OpenAI", "Anthropic", "Gemini"]}
                    value={identity.provider}
                    onChange={(_, val) =>
                      onIdentityChange("provider", val || "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Provider (optional)"
                        variant="filled"
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    label="Role"
                    variant="filled"
                    value={identity.role}
                    onChange={(e) => onIdentityChange("role", e.target.value)}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    variant="filled"
                    value={identity.description}
                    onChange={(e) =>
                      onIdentityChange("description", e.target.value)
                    }
                  />
                </Stack>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}
                >
                  Knowledge Space
                </Typography>
                <Autocomplete
                  multiple
                  options={knowledgeBase.docIndexes}
                  getOptionLabel={(opt: any) => opt.name || opt.index_id || "None"}
                  value={knowledgeBase.docIndexes.filter((index: any) =>
                    (knowledgeBase.selectedKnowledgeSpaceIds || []).includes(index.index_id),
                  )}
                  onChange={(_, value) =>
                    onKnowledgeBaseChange(value.map((index: any) => index.index_id).filter(Boolean))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Knowledge Spaces"
                      variant="filled"
                      helperText="Attach one or more governed spaces. Agents query them through query_knowledge."
                    />
                  )}
                />
              </Card>
            </Paper>
          </Box>

          {/* Selected Tools Section - Conditionally Rendered */}
          {tools.discoveredTools.length > 0 && (
            <Box sx={{ flex: 1, width: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "secondary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <ManageSearchIcon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ color: "primary.main" }}
                      >
                        Selected Tools ({tools.discoveredTools.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This agent can call these explicitly selected tools at runtime.
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={2}>
                    {tools.discoveredTools.map((tool, toolIndex) => {
                      const keysSchema = Array.isArray(tool?.keys_schema)
                        ? tool.keys_schema
                        : [];
                      const requiredKeyName = keysSchema[0]?.name;
                      const selectedKeyOption =
                        availableKeys.find((key: any) => key.name === tool.selectedKey) ||
                        (tool.selectedKey
                          ? {
                              name: tool.selectedKey,
                              display_name: tool.selectedKey,
                            }
                          : null);

                      return (
                        <Box
                          key={tool.tool_id || tool.name || toolIndex}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                          >
                            <Box sx={{ mt: 0.5 }}>
                              <CheckCircleIcon
                                color="success"
                                sx={{ fontSize: 20 }}
                              />
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                >
                                  {tool.display_name || tool.name}
                                </Typography>
                                <Chip
                                  label="Selected tool"
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                  }}
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                }}
                              >
                                {tool.description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 1 }}
                              >
                                {keysSchema.length > 0
                                  ? "Select the Key Manager credential this agent should use for this tool."
                                  : "No per-agent credential is required for this tool."}
                              </Typography>
                              {keysSchema.length > 0 && (
                                <Box sx={{ mt: 1.5, maxWidth: 420 }}>
                                  <Autocomplete
                                    options={availableKeys}
                                    getOptionLabel={(option: any) =>
                                      option?.display_name || option?.name || ""
                                    }
                                    value={selectedKeyOption}
                                    onChange={(_, value) =>
                                      tools.onToolKeyChange?.(toolIndex, value)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={`Select API Key for ${requiredKeyName || "tool"}`}
                                        size="small"
                                        required
                                        helperText="Choose a key from Key Manager for this tool"
                                      />
                                    )}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                  {hasMissingToolCredentials && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Select an API key for every key-bearing tool before creating this agent.
                    </Alert>
                  )}
                </Card>
              </Paper>
            </Box>
          )}
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 4, mb: 4 }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              size="large"
              variant="outlined"
              color={isEditMode ? "inherit" : "error"}
              onClick={onCancel}
              sx={{ borderRadius: 99, px: 4 }}
            >
              {isEditMode ? "Cancel" : "Discard Draft"}
            </Button>
          </Stack>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onCreate}
            disabled={isCreating || hasMissingToolCredentials}
            startIcon={
              isCreating ? <CircularProgress size={20} /> : <RocketLaunchIcon />
            }
            sx={{
              borderRadius: 99,
              px: 5,
              fontWeight: 700,
              boxShadow: "0 10px 30px rgba(184, 115, 51, 0.3)",
            }}
          >
            {isCreating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"}
          </Button>
        </Stack>
      </Box>
    </Fade>
  );
};

export default ExpressAgentReviewOrEdit;
